import { isValueExist, allValueExist } from '@util';

export const NOTI_TYPES = {
    SUCCESS: 'SUCCESS',
    FAIL: 'FAIL',
    NORMAL: 'NORMAL',
    CONNECTION_FAIL: 'CONNECTION_FAIL',
    LOADING: 'LOADING',
    IN_PROGRESS: 'IN_PROGRESS',
};

const { SUCCESS, FAIL } = NOTI_TYPES;

export default {
    namespaced: 'true',
    state: getDefaultState(),
    mutations: {
        setEditingDatum(state, datum) {
            state.editingDatum = datum;
        },
        setVisible(state, value) {
            if (state.visible !== value) {
                state.visible = value;
            }
        },
        addUpdateActions(state, action) {
            state.updateActions = R.assoc(
                action.type,
                action,
                state.updateActions
            );
        },
        removeUpdateActions(state, action) {
            state.updateActions = R.dissoc(action.type, state.updateActions);
        },
        resetUpdateActions(state) {
            state.updateActions = {};
        },
        updateUpdatesMeta(state, { tableId, data } = {}) {
            if (!isValueExist(data)) {
                return;
            }
            state.updatesMeta = R.assoc(tableId, data, state.updatesMeta);
        },
        deleteUpdatesMeta(state, tableId) {
            state.updatesMeta = R.dissoc(tableId, state.updatesMeta);
        },
        setPanelLoading(state, value) {
            state.panelLoading = value;
        },
        clear(state) {
            Object.assign(state, getDefaultState());
        },
        setBreakdownVisible(state, visible) {
            if (state.breakdownVisible !== visible) {
                state.breakdownVisible = visible;
            }
        },
        setBreakdownEditId(state, id) {
            state.breakdownEditId = id;
        },
        setBreakdownEditWidgetAssetIds(state, assetIds = []) {
            state.breakdownEditWidgetAssetIds = assetIds;
        },
        setIsAutoLabelingMode(state, value) {
            state.isAutoLabelingMode = value;
        },
        clearBreakdownEdit(state) {
            state.breakdownEditId = null;
            state.breakdownEditWidgetAssetIds = [];
            state.breakdownVisible = false;
        },
    },
    actions: {
        update(
            { commit, _, rootGetters },
            { actions = [], updateMeta = {}, listItem = {}, table }
        ) {
            const { tableId } = listItem;
            const client = rootGetters['user/dashboardClient'];
            let notiMsgs = rootGetters['view/notiMsgs'];

            // updateUpdatesMeta
            // deleteUpdatesMeta
            commit('updateUpdatesMeta', {
                tableId,
                data: {
                    ...updateMeta,
                    loading: true,
                    timestamp: new Date().getTime(),
                },
            });

            const ROOT_TRUE = {
                root: true,
            };

            const all = actions.filter(R.identity).map(async (action) => {
                try {
                    const data = await updateElement({
                        client,
                        row: listItem,
                        ...action,
                        table,
                    });

                    const updated = postProcess({ data, item: listItem });

                    if (updated) {
                        commit(
                            'newDashboard/mutateGridItem',
                            updated,
                            ROOT_TRUE
                        );
                    }

                    notiMsgs = [
                        ...notiMsgs,
                        {
                            type: SUCCESS,
                        },
                    ];

                    return listItem;
                } catch (error) {
                    // TODO add error msg
                    console.log({ error });

                    const refiendError = R.pathOr(
                        '',
                        ['response', 'data', 'error'],
                        error
                    );

                    const idx = R.findIndex(R.propEq('type', FAIL), notiMsgs);

                    let msg;

                    if (idx > -1) {
                        // case 1. already error msg exists
                        let fail = notiMsgs[idx];

                        notiMsgs = R.remove(idx, 1, notiMsgs);

                        msg = R.over(
                            R.lensProp('reasons'),
                            R.concat(R.__, [refiendError]),
                            fail
                        );
                    } else {
                        // case 2. no error message exists
                        msg = {
                            type: FAIL,
                            reasons: [refiendError],
                        };
                    }

                    notiMsgs = [...notiMsgs, msg];
                    throw new Error(
                        isValueExist(refiendError) ? refiendError : error
                    );
                }
            });

            return Promise.all(all)
                .then(R.head)
                .catch((error) => {
                    throw new Error(error);
                })
                .finally(() => {
                    commit('view/setNotiMsgs', notiMsgs, { root: true });

                    setTimeout(
                        () => commit('deleteUpdatesMeta', tableId),
                        Object.keys(updateMeta).length === 1 &&
                            notiMsgs.length === 0
                            ? 700
                            : 0
                    );
                });
        },
        prepareUpdate(
            { commit },
            { action, datum, meta: { path, origin } = {} }
        ) {
            let originValue = -1,
                newValue = -2;

            if (allValueExist(path, origin)) {
                originValue = R.path(path, origin);
                newValue = R.path(path, datum);
            }
            // check it's same as origin data
            originValue == newValue
                ? commit('removeUpdateActions', action)
                : commit('addUpdateActions', action);

            // keep updating datum
            commit('setEditingDatum', datum);
        },
    },
    getters: {
        campaigns: R.compose(
            R.defaultTo([]),
            R.prop('newDashboard/campaigns'),
            R.nthArg(3)
        ),
        updateMetaByTableId:
            ({ updatesMeta }) =>
            (tableId) =>
                updatesMeta[tableId],
    },
};

function getDefaultState() {
    return {
        visible: false,
        editingDatum: {},
        updatesMeta: {},
        updateActions: {},
        panelLoading: false,
        breakdownVisible: false,
        breakdownEditId: null,
        breakdownEditWidgetAssetIds: [],
        isAutoLabelingMode: false,
    };
}

function updateElement(data) {
    const { client } = data;
    return client.updateElement(data);
}

function postProcess({ data = {}, item = {} }) {
    const { value, type } = data;

    switch (type) {
        case 'running': {
            Object.assign(item, {
                active: value,
            });
            return item;
        }
        case 'schedule': {
            Object.assign(item, {
                cell: {
                    ...item.cell,
                    start: value.startDate,
                    ends: value.endDate,
                },
                [type]: value,
            });
            return item;
        }
        case 'budget': {
            Object.assign(item, {
                budget: {
                    ...value,
                    rawAmount: value.amount,
                    isTotalSum: value.isLifeTimeBudget,
                },
            });
            return item;
        }
        case 'keywordBid': {
            // Object.assign(item, {
            //     cell: {
            //         ...item.cell,
            //         bidCap: value.amount,
            //     },
            // });
            return item;
        }
        default: {
            Object.assign(item, {
                [type]: value,
            });
            return item;
        }
    }
}
